import { get } from "lodash-es";
import { MESApiService, getMomentTz, spParameter } from "../../../../services";
import type { SectionEntry } from "../models/SectionEntry";

const moment = getMomentTz();

export const getEntriesBySection = async (
	params: {
		sectionId: number;
	},
	signal?: AbortSignal,
): Promise<SectionEntry[]> => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "SectionId", value: params.sectionId },
	];

	const resp = await apiService.callV2(
		"[SWV].[GetEntriesBySection]",
		parameters,
		signal,
	);

	if (resp.ok) {
		let rows = get(resp, "data.tables[0].rows", []) as SectionEntry[];

		rows = rows.map((entry) => ({
			...entry,
			CreateTimestamp: entry.CreateTimestamp
				? moment.utc(entry.CreateTimestamp).toDate()
				: null,
			UpdateTimestamp: entry.UpdateTimestamp
				? moment.utc(entry.UpdateTimestamp).toDate()
				: null,
			SectionEntryCreatedAt: entry.SectionEntryCreatedAt
				? moment.utc(entry.SectionEntryCreatedAt).toDate()
				: null,
			SectionEntryUpdatedAt: entry.SectionEntryUpdatedAt
				? moment.utc(entry.SectionEntryUpdatedAt).toDate()
				: null,
		}));

		return rows;
	} else {
		throw new Error(resp.message || "Error fetching entries by section");
	}
};

export const upsertSectionEntry = async ({
	entryId,
	sectionId,
	weighUp,
	breakIn,
	notes,
}: {
	entryId: number;
	sectionId: number;
	weighUp?: string | null;
	breakIn?: string | null;
	notes?: string | null;
}): Promise<SectionEntry | null> => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "EntryId", value: entryId },
		{ name: "SectionId", value: sectionId },
	];

	if (weighUp !== undefined) {
		parameters.push({ name: "WeighUp", value: weighUp });
	}

	if (breakIn !== undefined) {
		parameters.push({ name: "BreakIn", value: breakIn });
	}

	if (notes !== undefined) {
		parameters.push({ name: "Notes", value: notes });
	}

	const resp = await apiService.callV2(
		"[SWV].[UpsertLogSectionEntry]",
		parameters,
	);

	if (resp.ok) {
		return get(resp, "data.tables[0].rows[0]", null) as SectionEntry | null;
	} else {
		throw new Error(resp.message || "Error upserting section entry");
	}
};

export const deleteSectionEntry = async (entryId: number): Promise<void> => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [{ name: "EntryId", value: entryId }];

	const resp = await apiService.callV2(
		"[SWV].[DeleteLogSectionEntry]",
		parameters,
	);

	if (!resp.ok) {
		throw new Error(resp.message || "Error deleting section entry");
	}
};

export const createSectionEntry = async ({
	assetId,
	shiftId,
	sectionId,
	title,
	content,
	createTimestamp,
	author,
	weighUp,
	breakIn,
	notes,
}: {
	assetId: number | null;
	shiftId: number;
	sectionId: number;
	title: string;
	content: string;
	createTimestamp: Date;
	author: string;
	weighUp?: string | null;
	breakIn?: string | null;
	notes?: string | null;
}): Promise<SectionEntry | null> => {
	const apiService = new MESApiService();

	const entryParameters: spParameter[] = [
		{ name: "ShiftId", value: shiftId },
		{ name: "Title", value: title },
		{ name: "Content", value: content },
		{ name: "CreateTimestamp", value: createTimestamp.toISOString() },
		{ name: "Author", value: author },
		{ name: "EntryTypeCode", value: "S" },
	];

	if (assetId || assetId === 0) {
		entryParameters.push({ name: "AssetId", value: assetId });
	}

	const entryResp = await apiService.callV2(
		"[LB].[UpsertEntry]",
		entryParameters,
	);

	if (!entryResp.ok) {
		throw new Error(entryResp.message || "Error creating entry");
	}

	const createdEntry = get(entryResp, "data.tables[0].rows[0]", null);
	if (!createdEntry?.EntryId) {
		throw new Error("Failed to get created entry ID");
	}

	const sectionParameters: spParameter[] = [
		{ name: "EntryId", value: createdEntry.EntryId },
		{ name: "SectionId", value: sectionId },
	];

	if (weighUp !== undefined) {
		sectionParameters.push({ name: "WeighUp", value: weighUp });
	}

	if (breakIn !== undefined) {
		sectionParameters.push({ name: "BreakIn", value: breakIn });
	}

	if (notes !== undefined) {
		sectionParameters.push({ name: "Notes", value: notes });
	}

	const sectionResp = await apiService.callV2(
		"[SWV].[UpsertLogSectionEntry]",
		sectionParameters,
	);

	if (!sectionResp.ok) {
		throw new Error(sectionResp.message || "Error linking entry to section");
	}

	const entries = await getEntriesBySection({
		sectionId,
	});

	return entries.find((e) => e.EntryId === createdEntry.EntryId) || null;
};
